import { useEffect, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Check, Loader2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import { motion } from "framer-motion"
import type { Review } from "@/services/api/odin/activities/diagnostic/study-session/update"
import { cn } from "@/lib/utils"

interface MultipleChoiceQuestionProps {
  isLoading: boolean
  displayReview: boolean
  review: Review
  options: string[]
  onClickSendAnswer: (correctAnswer: number) => void
  onClickNextQuestion: () => void
}

export function MultipleChoiceQuestion({
  isLoading,
  displayReview,
  options,
  review,
  onClickSendAnswer,
  onClickNextQuestion,
}: MultipleChoiceQuestionProps) {
  const [isError, setIsError] = useState(false)
  const [optionIndex, setOptionIndex] = useState<number | null>(null)
  const isCorrectAnswer = review.correctAnswerIndex === optionIndex

  useEffect(() => {
    setOptionIndex(null)
    setIsError(false)
  }, [options])

  const handleOnClickOption = (index: number) => () => {
    if (displayReview || isLoading) return
    setOptionIndex(index)
    setIsError(false)
  }

  const handleOnNext = (index: number) => () => {
    if (optionIndex === null) {
      setIsError(true)
      return
    }

    if (displayReview) {
      onClickNextQuestion()
      return
    }

    onClickSendAnswer(index)
  }

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
      },
    },
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-3">
        {options.map((option, index) => {
          const selected = optionIndex === index
          const isCorrect = review.correctAnswerIndex === index

          return (
            <Card
              key={index}
              onClick={handleOnClickOption(index)}
              className={cn(
                "cursor-pointer p-4 shadow-sm",
                selected ? "bg-neutral-200" : "bg-white",
                displayReview || isLoading ? "cursor-default" : "cursor-pointer",
              )}
            >
              <div className="flex h-8 items-center justify-between">
                <div className="flex flex-1 items-center gap-2">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-700/15 text-sm font-medium text-neutral-700">
                    {String.fromCharCode(65 + index)}
                  </span>
                  <p className="text-sm text-neutral-700">{option}</p>
                </div>
                <span
                  style={{ display: displayReview ? "flex" : "none" }}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-full p-1",
                    isCorrect ? "bg-green-600" : "bg-red-700",
                  )}
                >
                  {isCorrect ? (
                    <Check className="h-4 w-4 text-white" strokeWidth={2.5} />
                  ) : (
                    <X className="h-4 w-4 text-white" strokeWidth={2.5} />
                  )}
                </span>
              </div>
            </Card>
          )
        })}
      </div>
      <div className="flex items-center justify-between">
        <div className="text-sm text-red-500">{isError && "Selecciona una opción."}</div>
        <Button onClick={handleOnNext(optionIndex || 0)}>
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : displayReview ? (
            "Continuar"
          ) : (
            "Enviar"
          )}
        </Button>
      </div>
      <motion.div
        variants={cardVariants}
        initial="hidden"
        animate={displayReview ? "visible" : "hidden"}
        exit="exit"
        className="w-full pt-3"
      >
        <Card className="relative w-full overflow-hidden rounded-md">
          <div
            className={cn(
              "absolute left-0 top-0 h-full w-2",
              isCorrectAnswer ? "bg-green-600" : "bg-red-600",
            )}
          />
          <CardContent className={cn("p-4", isCorrectAnswer ? "bg-green-500/5" : "bg-red-500/5")}>
            <p className={cn("text-sm", isCorrectAnswer ? "text-green-600" : "text-red-600")}>
              {review.review}
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
